import { LINK_STATE_LABELS, type LinkState } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check, Copy, ExternalLink, Plus } from 'lucide-react'
import type { BadgeVariant } from '@/components/ui/field'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { buttonVariants } from '@/components/ui/button'
import { Badge, Card, Skeleton } from '@/components/ui/field'
import { Checkbox } from '@/components/ui/select'
import { linksQuery, publicUrlFor, type RegistrationLink } from '@/features/links/api'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../_app'

export const Route = createFileRoute('/_app/links/')({
  component: LinksPage,
})

const STATE_TONE: Record<LinkState, BadgeVariant> = {
  open: 'success',
  scheduled: 'default',
  closed: 'muted',
  capped: 'destructive',
  disabled: 'muted',
}

function LinksPage() {
  const { me } = AppRoute.useRouteContext()
  const [includeInactive, setIncludeInactive] = useState(true)
  const { data, isLoading, isError } = useQuery(linksQuery(includeInactive))

  const items = data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Registration links</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Shareable forms that let clients register themselves.
          </p>
        </div>
        {me.permissions.includes('links:create') && (
          <Link to="/links/new" className={buttonVariants()}>
            <Plus className="h-4 w-4" />
            New link
          </Link>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Show inactive links
      </label>

      {isLoading && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}
      {isError && (
        <Card className="p-8 text-center text-sm text-[var(--color-destructive)]">
          Couldn&rsquo;t load links.
        </Card>
      )}
      {data && items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <ExternalLink className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm font-medium">No registration links yet</p>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Create one to let clients register themselves from a phone.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {items.map((link) => (
          <LinkCard key={link.id} link={link} />
        ))}
      </div>
    </div>
  )
}

function LinkCard({ link }: { link: RegistrationLink }) {
  const [copied, setCopied] = useState(false)
  const url = publicUrlFor(link.slug)

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard is blocked outside a secure context / without permission —
      // the URL is on screen either way, so this is not worth an error state.
    }
  }

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/links/$linkId"
            params={{ linkId: link.id }}
            className="font-medium text-[var(--color-primary)] hover:underline"
          >
            {link.title}
          </Link>
          <p className="mt-0.5 truncate font-mono text-xs text-[var(--color-muted-foreground)]">
            /r/{link.slug}
          </p>
        </div>
        <Badge variant={STATE_TONE[link.state]}>{LINK_STATE_LABELS[link.state]}</Badge>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-[var(--color-muted-foreground)]">Registrations</dt>
          <dd className="font-medium">
            {link.registrationCount}
            {link.maxRegistrations !== null && ` / ${link.maxRegistrations}`}
          </dd>
        </div>
        <div>
          <dt className="text-[var(--color-muted-foreground)]">
            {link.state === 'scheduled' ? 'Opens' : 'Closes'}
          </dt>
          <dd className="font-medium">
            {link.state === 'scheduled'
              ? formatDateTime(link.opensAt)
              : link.closesAt
                ? formatDateTime(link.closesAt)
                : 'No end date'}
          </dd>
        </div>
      </dl>

      {/* A cap is the thing most likely to close a drive unexpectedly, so it
          gets a progress bar rather than a number to squint at. */}
      {link.maxRegistrations !== null && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-muted)]">
          <div
            className={cn(
              'h-full',
              link.state === 'capped'
                ? 'bg-[var(--color-destructive)]'
                : 'bg-[var(--color-primary)]',
            )}
            style={{
              width: `${Math.min(100, (link.registrationCount / link.maxRegistrations) * 100)}%`,
            }}
          />
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-muted)]"
        >
          {copied ? (
            <Check className="h-3 w-3 text-[var(--color-success)]" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-muted)]"
        >
          <ExternalLink className="h-3 w-3" />
          Preview
        </a>
      </div>
    </Card>
  )
}
